import Button from 'components/buttons/Button';
import { ButtonRowRight, FlexColSm, FlexMd } from 'components/Elements';
import useNeveForm from 'components/forms/NeveForm';
import TextField from 'components/forms/TextField';
import useModifyRequest from 'hooks/useModifyRequest';
import React from 'react';
import { createErrorMessage, createGlobalErrorFromAPIError, requiredFieldErrorMessage } from 'shared/error-messages';
import strings from '../../../strings/strings.json';
import { ErrorNotifierSlim } from 'components/notifiers/ErrorNotifierSlim';
import { styled } from 'styled-components';
import RadioFields from 'components/forms/RadioFields';
import { ROLE_DETAILS, RoleType } from 'models/User';
import { FormLabel } from 'components/forms/FormComponents';
import { sizes } from 'theme';
import { ConditionalValidationErrorMessage } from 'components/forms/ValidationErrorMessage';

type InviteUserFormInput = {
    firstName: string;
    lastName: string;
    emailAddress: string;
    role: RoleType | null;
};

type InviteTrainerFormProps = {
    closeModal: (trainerId: string) => Promise<void>;
};

export function InviteUserForm({ closeModal }: InviteTrainerFormProps): JSX.Element {
    const [errorMessage, setErrorMessage] = React.useState<string>('');
    const [roleError, setRoleError] = React.useState<boolean>(false);

    const { modifyData: inviteUser, loading: inviting } = useModifyRequest(`organisations/invite-user`, 'POST');

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useNeveForm<InviteUserFormInput>({
        firstName: '',
        lastName: '',
        emailAddress: '',
        role: null,
    });

    async function onSubmit(formData: InviteUserFormInput) {
        if (!formData.role) {
            setRoleError(true);
            return;
        }

        const invitation = {
            inviteeFirstName: formData.firstName,
            inviteeLastName: formData.lastName,
            inviteeEmail: formData.emailAddress,
            inviteeRole: formData.role,
        };

        const response = await inviteUser(invitation);

        if (response.errors && response.errors.length) {
            let responseErrorMessage =
                response.errors[0] && response.errors[0].code
                    ? createGlobalErrorFromAPIError(response.errors[0].code)
                    : strings.inviteTrainerForm.errorMessage.title;
            setErrorMessage(responseErrorMessage);
        } else {
            const userId = response.value as string;
            closeModal(userId);
        }
    }

    return (
        <Form onSubmit={handleSubmit(onSubmit)}>
            <FlexColSm>
                <FlexMd>
                    <NameTextField>
                        <TextField
                            fieldName={'firstName'}
                            labelText={strings.inviteTrainerForm.fields.firstName.text}
                            inputAria={strings.inviteTrainerForm.fields.firstName.aria}
                            errorMessage={errors.firstName?.message}
                            required
                            {...register('firstName', {
                                required: {
                                    value: true,
                                    message: requiredFieldErrorMessage(
                                        strings.inviteTrainerForm.fields.firstName.text,
                                    ),
                                },
                            })}
                        />
                    </NameTextField>
                    <NameTextField>
                        <TextField
                            fieldName={'lastName'}
                            labelText={strings.inviteTrainerForm.fields.lastName.text}
                            inputAria={strings.inviteTrainerForm.fields.lastName.aria}
                            errorMessage={errors.lastName?.message}
                            required
                            {...register('lastName', {
                                required: {
                                    value: true,
                                    message: requiredFieldErrorMessage(strings.inviteTrainerForm.fields.lastName.text),
                                },
                            })}
                        />
                    </NameTextField>
                </FlexMd>
                <TextField
                    fieldName={'emailAddress'}
                    labelText={strings.inviteTrainerForm.fields.emailAddress.text}
                    inputAria={strings.inviteTrainerForm.fields.emailAddress.aria}
                    errorMessage={errors.emailAddress?.message}
                    required
                    {...register('emailAddress', {
                        required: {
                            value: true,
                            message: requiredFieldErrorMessage(strings.inviteTrainerForm.fields.emailAddress.text),
                        },
                        pattern: {
                            value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                            message: createErrorMessage(
                                strings.inviteTrainerForm.fields.emailAddress.text,
                                strings.errorMessages.fieldErrorInvalidEmail,
                            ),
                        },
                    })}
                />
            </FlexColSm>

            <FlexColSm>
                <FormLabel>
                    {strings.inviteTrainerForm.fields.role.text}
                    {' *'}
                </FormLabel>
                <RadioFields
                    options={ROLE_DETAILS.filter(
                        (r) =>
                            r.name === RoleType.ORGANISATION_ADMIN ||
                            r.name === RoleType.TRAINER ||
                            r.name === RoleType.LEARNER,
                    ).map((roleDetail) => ({
                        value: roleDetail.name,
                        title: roleDetail.friendlyName,
                        description: roleDetail.description,
                        aria: roleDetail.name,
                        inputProps: register('role'),
                        name: 'role',
                    }))}
                />
                {roleError && (
                    <ConditionalValidationErrorMessage error={strings.inviteTrainerForm.fields.role.error} />
                )}
            </FlexColSm>
            <ButtonRowRight>
                {errorMessage && <ErrorNotifierSlim description={errorMessage} />}
                <Button
                    type="submit"
                    title={strings.inviteTrainerForm.submitButton.title}
                    aria={strings.inviteTrainerForm.submitButton.aria}
                    submitting={inviting}
                />
            </ButtonRowRight>
        </Form>
    );
}

const Form = styled.form`
    display: flex;
    flex-direction: column;
    gap: ${sizes.spacingMd};
`;

const NameTextField = styled.div`
    flex: 1;
`;
